import { forwardRef, FunctionComponent, InputHTMLAttributes, useContext } from "react";
import { useFormikContext } from "formik";
import { RefType } from "../../../../types";
import { genericMemo } from "../../../../utils";
import { FieldsStateContext } from "../../validation/FieldsStateContext";
import { InputProps } from "../Input";
import { InputPropsRequiredProps, InputWithState, InputWithStateProps } from "../InputWithState";

export type MimicInputWithCheckProps<
  P extends object = InputHTMLAttributes<HTMLInputElement>,
  R extends unknown = HTMLInputElement,
  TProps extends InputPropsRequiredProps = InputProps<P, R>
> = InputWithStateProps<P, R, TProps> & {
  name?: string
}
export type MimicInputWithStateSpecifiedType<T extends object> = FunctionComponent<MimicInputWithCheckProps<T>>;
const InputComponent = <
  P extends object = InputHTMLAttributes<HTMLInputElement>,
  R extends unknown = HTMLInputElement,
  TProps extends InputPropsRequiredProps = InputProps<P, R>
>(props: MimicInputWithCheckProps<P, R, TProps>) => <></>;
type InputComponentType = typeof InputComponent;

export const MimicInputWithState = genericMemo(forwardRef(<
  P extends object = InputHTMLAttributes<HTMLInputElement>,
  R extends unknown = HTMLInputElement,
  TProps extends InputPropsRequiredProps = InputProps<P, R>
>(
  props: MimicInputWithCheckProps<P, R, TProps>,
  ref: RefType<R>,
) => {
  const fieldStates = useContext(FieldsStateContext);
  const { getFieldMeta, submitCount } = useFormikContext();
  const fieldMeta = getFieldMeta(props.name!);
  const shouldBeShown = submitCount > 0 || fieldMeta.touched;

  if (!props.name) {
    throw new Error("[MimicInputWithState] name is required");
  }

  const fieldState = fieldStates[props.name];

  return (
    <InputWithState
      state={shouldBeShown ? fieldState?.state : undefined}
      stateText={shouldBeShown ? fieldState?.stateText : undefined}
      {...props}
      ref={ref}
    />
  );
}) as InputComponentType);

export default MimicInputWithState;